// SourceColumnSetManage.jsx

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import AIPanel from "../../../components/AIChat/AIPanel";
import { DialogControl, IconControl, Loading } from "spark-core-dx/components";
import { useCoreContext, useCoreStateContext } from "spark-core-dx/contexts";
import { useCoreRoute, useGenericManager, useOperationDialog } from "spark-core-dx/hooks";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import ColumnSetEditor from "../../../components/ColumnSetEditor";
import "./sourceColumnSetManage.scss";

const firstItem = (value) => {
  if (!value) return null;
  if (typeof value?.first === "function") return value.first();
  return Array.isArray(value) ? value[0] : null;
};

const getSourceObjectId = (value) =>
  value?.SourceObjectId || value?._id || value?.UniqueId || value?.Id || value?.id || null;

const SOURCE_OBJECT_ITEM_NAME = "SourceObjectItem";
const EXCLUDED_SOURCE_OBJECT_OPERATION_IDS = new Set([
  "ManageSourceObject",
  "AIQuestionSourceObjectManage",
  "AIQuestionEDIDiagnostics",
]);
const EXCLUDED_SOURCE_OBJECT_OPERATION_LABELS = new Set([
  "Manage Source",
  "Manage Source Object",
  "Manage Source Configuration",
  "Analyze EDI Diagnostics",
]);

const getOperationName = (operation) => operation?.Name || operation?.OperationTypeId || operation?.Value || operation?._id || operation?.Id || "";

const getOperationDisplayName = (operation) => operation?.DisplayName || operation?.Text || getOperationName(operation);

const isSourceObjectOperation = (operation) =>
  operation?.SupportsOperationItem === true &&
  (operation?.ItemName === SOURCE_OBJECT_ITEM_NAME || operation?.MetadataRefItemName === SOURCE_OBJECT_ITEM_NAME);

const isExcludedSourceObjectOperation = (operation) =>
  EXCLUDED_SOURCE_OBJECT_OPERATION_IDS.has(getOperationName(operation)) ||
  EXCLUDED_SOURCE_OBJECT_OPERATION_LABELS.has(getOperationDisplayName(operation));

const normalizeOperationForDialog = (operation) => {
  const name = getOperationName(operation);
  return {
    ...operation,
    Name: name,
    OperationTypeId: operation?.OperationTypeId || name,
    DisplayName: getOperationDisplayName(operation),
  };
};

const SourceColumnSetManage = () => {
  const itemService = useMemo(() => new ItemServiceFactory(), []);
  const itemServiceEnum = itemService?.ItemServiceEnum ?? {
    SourceObject: "SourceObject",
    AIChatSession: "AIChatSession",
  };
  const { coreNavigate } = useCoreRoute();
  const coreState = useCoreStateContext();
  const coreContext = useCoreContext();
  const pageRef = useRef(null);

  const sourceObjectMgr = useGenericManager(itemServiceEnum.SourceObject);
  const chatSessionMgr = useGenericManager(itemServiceEnum.AIChatSession);
  const {
    handleOperationDialog,
    activeOperation,
    operationSuccessMessage,
    operationErrors,
    cancelOperation,
    renderOperationContent,
    executeOperation,
    openDialog,
  } = useOperationDialog(itemServiceEnum.SourceObject);
  const sourceObjectMgrRef = useRef(sourceObjectMgr);
  const chatSessionMgrRef = useRef(chatSessionMgr);
  const coreStateRef = useRef(coreState);
  sourceObjectMgrRef.current = sourceObjectMgr;
  chatSessionMgrRef.current = chatSessionMgr;
  coreStateRef.current = coreState;

  const [sourceObject, setSourceObject] = useState(null);
  const [currentSourceObject, setCurrentSourceObject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refresh, setRefresh] = useState("0");
  const [session, setSession] = useState(null);
  const [metadata, setMetadata] = useState(null);

  const { id } = useParams();
  const lastPayloadStrRef = useRef("");
  const loadKeyRef = useRef(null);

  const sourceObjectOperations = useMemo(() => {
    const operations = Array.isArray(coreContext?.Operations) ? coreContext.Operations : [];
    return operations
      .filter(isSourceObjectOperation)
      .filter((operation) => !isExcludedSourceObjectOperation(operation))
      .map(normalizeOperationForDialog)
      .sort((a, b) => {
        const sequenceA = Number.isFinite(Number(a?.Sequence)) ? Number(a.Sequence) : 9999;
        const sequenceB = Number.isFinite(Number(b?.Sequence)) ? Number(b.Sequence) : 9999;
        if (sequenceA !== sequenceB) return sequenceA - sequenceB;
        return getOperationDisplayName(a).localeCompare(getOperationDisplayName(b));
      });
  }, [coreContext?.Operations]);

  const setCurrentSourceObjectIfChanged = useCallback((next) => {
    let nextStr = "";
    try {
      nextStr = JSON.stringify(next);
    } catch {
      /* ignore */
    }

    if (nextStr && nextStr === lastPayloadStrRef.current) return;

    setCurrentSourceObject((prev) => {
      try {
        const prevStr = JSON.stringify(prev);
        if (prevStr === nextStr) return prev;
      } catch {
        if (prev === next) return prev;
      }
      lastPayloadStrRef.current = nextStr || "";
      return next;
    });
  }, []);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return undefined;
    }

    if (loadKeyRef.current === id) {
      return undefined;
    }

    loadKeyRef.current = id;
    let cancelled = false;

    const getChatSession = async (referenceId) => {
      if (!referenceId) return null;

      const chatSessionResult = await chatSessionMgrRef.current.Query({
        model:
          "x => x.ReferenceId == referenceId && x.ReferenceItemName == referenceItemName && x.IsDeleted == false && x.IsActive == true",
        params: { referenceId, referenceItemName: "SourceObjectManage" },
      });

      if (chatSessionResult?.Success && chatSessionResult?.Items?.length > 0) {
        return firstItem(chatSessionResult.Items);
      }
      return null;
    };

    const load = async () => {
      setIsLoading(true);

      try {
        let nextSourceObject = null;
        const redirectData = coreStateRef.current?.get?.("InternalRedirectData");
        if (redirectData?.SourceObjectId && (id === redirectData._id || id === redirectData.SourceObjectId)) {
          nextSourceObject = redirectData;
        } else {
          const result = await sourceObjectMgrRef.current.getItem(id);
          if (result?.Success && result.Items?.length > 0) {
            nextSourceObject = firstItem(result.Items);
          }
        }

        if (!cancelled && nextSourceObject) {
          setSourceObject(nextSourceObject);
          setCurrentSourceObjectIfChanged(nextSourceObject);
          const nextSession = await getChatSession(getSourceObjectId(nextSourceObject));
          if (!cancelled) setSession(nextSession);
        }

        const metadataResp = await sourceObjectMgrRef.current.GetMetadata({});
        if (!cancelled) setMetadata(metadataResp?.Items ?? []);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id, setCurrentSourceObjectIfChanged]);

  const onUpdatePanel = async (UICommand, item, value) => {
    if (UICommand !== "update") return;

    const normalizeDraftPayload = (candidate) => {
      if (!candidate || typeof candidate !== "object") return null;
      if (Array.isArray(candidate.Fields)) return candidate;
      if (Array.isArray(candidate.fields)) return { ...candidate, Fields: candidate.fields };
      return null;
    };

    const updatedSourceObject =
      normalizeDraftPayload(item) ??
      normalizeDraftPayload(value) ??
      normalizeDraftPayload(value?.sourceObject) ??
      normalizeDraftPayload(value?.output) ??
      normalizeDraftPayload(value?.output?.sourceObject) ??
      null;

    if (updatedSourceObject?.Fields) {
      setCurrentSourceObjectIfChanged(updatedSourceObject);
      setRefresh({ ...updatedSourceObject, acceptedAt: Date.now() });
    }
  };

  const handleSaveSourceObject = async (updatedSourceObject) => {
    const result = await sourceObjectMgr.saveItem(updatedSourceObject);
    if (result?.Success) {
      const saved = firstItem(result.Items);
      setRefresh(String(Date.now()));
      setSourceObject(saved);
      setCurrentSourceObjectIfChanged(saved);
    }
    return result;
  };

  const handleCurrentSourceObject = (value) => {
    setCurrentSourceObjectIfChanged(value);
  };

  const handleSourceOperationClick = async (operation) => {
    if (!operation || !sourceObject) return;

    await handleOperationDialog(
      {
        ...operation,
        itemData: currentSourceObject || sourceObject,
      },
      { closeOnComplete: false }
    );
  };

  const sourceObjectId = getSourceObjectId(sourceObject) || id;

  if (isLoading) {
    return (
      <div className="centered-loading">
        <Loading />
      </div>
    );
  }

  return (
    <div className="source-column-set-screen">
      <div className="source-column-set-screen__header">
        <h2 className="source-column-set-screen__title">
          <IconControl iconKey={"schema"} /> Source Object Designer
        </h2>
        <div className="source-column-set-screen__header-actions">
          <button type="button" className="source-column-set-screen__back" onClick={() => coreNavigate("/dynamic/sourceobject/")}>
            {"<<"} Back to Source Object List
          </button>
        </div>
      </div>
      <DialogControl
        controlOrigin="source-object-execute-operation"
        openDialog={openDialog}
        title={activeOperation?.DisplayName ?? activeOperation?.Name ?? "Operation Completed"}
        subTitle={activeOperation?.Details}
        onCancel={cancelOperation}
        onOk={async () => executeOperation()}
        excludeCancel={!activeOperation}
        okText={activeOperation && operationErrors ? "Retry" : "OK"}
        disableCancel={operationSuccessMessage}
      >
        {renderOperationContent()}
      </DialogControl>
      <AIPanel
        childrenRef={pageRef}
        open={true}
        presentation="inline"
        onUpdatePanel={onUpdatePanel}
        mapToClass="core-layout"
        params={{ id: sourceObjectId, sourceObjectId }}
        session={session}
        payload={currentSourceObject}
        referenceId={sourceObjectId}
        referenceName="SourceObjectManage"
        operationName="AIQuestionSourceObjectManage"
        defaultOrchestrationMode="AgentAssist"
        singleSessionMode={true}
        planningPreviewEnabled={false}
        enableContentDragHiding={false}
        minLeft={825}
        minRight={400}
        startChat={null}
        splashMessage="How can I help you manage this source object?"
        introMessage="Manage Source Configuration..."
        onCancel={() => {
          coreNavigate("/dynamic/sourceobject/");
        }}
      >
        <div className="column-set-editor">
          <ColumnSetEditor
            sourceObject={sourceObject}
            fields={sourceObject?.Fields ?? []}
            fieldItemMetadata={metadata?.find((x) => x.Name === "SourceObjectField")}
            sourceObjectItemMetadata={metadata?.find((x) => x.Name === "SourceObject")}
            onSave={handleSaveSourceObject}
            onChange={handleCurrentSourceObject}
            refresh={refresh}
            sourceObjectOperations={sourceObjectOperations}
            onSourceObjectOperationClick={handleSourceOperationClick}
          />
        </div>
      </AIPanel>
    </div>
  );
};

export default SourceColumnSetManage;
